/* eslint-disable max-lines-per-function */
import React from "react";
import { Link } from "@reach/router";
import { ApolloQueryResult, OperationVariables, useMutation } from "@apollo/client";
import css from "classnames";
import { gql } from "graphql-tag";
import MaterialTable, { Column } from "@material-table/core";
import { Grid } from "@material-ui/core";
import { useSnackbar } from "components/haven-design/snackbar";
import tooltip from "components/tooltip/tooltip.module.css";
import { DuplicateTemplateGroup,
  DuplicateViewOnlyTemplateGroupVariables,
  templateGroups_templateGroups as Template,
  templateGroups } from "types/gql-types";
import logger from "services/logger";
import { Viewer } from "components/auth/types";
import { grabEmailTemplate, grabLetterTemplate, grabSmsTemplate } from "utils/template";
import styles from "./template-table.module.css";
import { RemoveTemplate } from "./remove";

const selectPageSize = (desiredPageSize: number, pageSizeOptions: number[]): number => {
  let left = 0;
  let right = pageSizeOptions.length - 1;
  let result = null;

  while (left <= right) {
    const mid = Math.floor((left + right) / 2);

    if (pageSizeOptions[mid] > desiredPageSize) {
      result = pageSizeOptions[mid];
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }

  return result || pageSizeOptions[0];
};

type RefetchTemplates = (variables?: Partial<OperationVariables> | undefined) =>
Promise<ApolloQueryResult<templateGroups>>
const DUPLICATE_TEMPLATE = gql`
  mutation DuplicateTemplateGroup($id: ID!) {
    duplicateTemplateGroup(id: $id) {
      id
    }
  }
`;

const EditableActionColumn: React.FC<{
  duplicateAction: () => void
  rowData: Template,
}> = ({
  rowData,
  duplicateAction,
}) => (
  <div style={{ whiteSpace: "nowrap" }}>
    <span style={{ padding: 2 }}>
      <Link to={`/dash/manager/settings/templates/update/${rowData.id}`}>Edit</Link> |
    </span>
    <span style={{ padding: 2 }}>
      <RemoveTemplate template={rowData} /> |
    </span>
    <span style={{ padding: 2 }}>
      <a
        href="#"
        onClick={(e) => {
          e.preventDefault();
          duplicateAction();
        }}
      >
          Copy
      </a>
    </span>
  </div>
);

const ActionColumn = (props: any) => {
  const { rowData, viewer, refetchTemplates } = props;
  const ownsTemplate = rowData.userId === viewer.id;
  const supervisorEditable = viewer.isSupervisor && rowData.isOrgTemplate;
  const [duplicate] = useMutation<DuplicateTemplateGroup, DuplicateViewOnlyTemplateGroupVariables>(
    DUPLICATE_TEMPLATE,
  );
  const { openSnackbar } = useSnackbar();
  const duplicateAction = () =>
    duplicate({ variables: { id: rowData.id } })
      .then(() => refetchTemplates())
      .catch((err: any) => {
        logger.sendError(err);
        openSnackbar({ message: "Error copying template", status: "error" });
      });

  if (ownsTemplate || supervisorEditable || rowData.canModify) {
    return <EditableActionColumn {...props} duplicateAction={duplicateAction} />;
  }

  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <span style={{ padding: 2 }}>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            duplicateAction();
          }}
        >
          Copy
        </a>
      </span>
    </div>
  );
};

const SmsColumn = (rowData: Template) => {
  const smsTemplate = grabSmsTemplate(rowData.templates);
  if (smsTemplate && !smsTemplate.isEmpty) {
    return <div className="fa fa-mobile" />;
  }
  return null;
};

const EmailColumn = (rowData: Template) => {
  const emailTemplate = grabEmailTemplate(rowData.templates);
  if (emailTemplate && !emailTemplate.isEmpty) {
    return <div className="fa fa-inbox" />;
  }
  return null;
};

const getUpdatedAt = (rowData: Template) => {
  const dates = rowData.templates
    .map(({ updatedAt }) => (updatedAt === null ? 0 : new Date(updatedAt).getTime()));
  return Math.max(...dates);
};

const UpdatedAtColumn = (rowData: Template) => {
  const maxDate = getUpdatedAt(rowData);
  if (maxDate) return new Date(maxDate).toDateString();
  return null;
};

const sortUpdatedAt = (a: Template, b: Template) => getUpdatedAt(a) - getUpdatedAt(b);

const MailColumn = (rowData: Template) => {
  const mailTemplate = grabLetterTemplate(rowData.templates);
  if (mailTemplate && !mailTemplate.isEmpty) {
    return <div className="fa fa-envelope" />;
  }
  return null;
};

const getOwnershipTypeSortKey = ({ name, isOrgTemplate, isPropertyTemplate }: Template) => {
  // Create the sort key combining the access type
  // and the template name in a string
  let ownershipIndex = 2;
  if (isOrgTemplate) {
    ownershipIndex = 0;
  } else if (isPropertyTemplate) {
    ownershipIndex = 1;
  }

  return `${ownershipIndex} || ${name}`;
};

const sortOwnershipType = (a: Template, b: Template) =>
  getOwnershipTypeSortKey(a).localeCompare(getOwnershipTypeSortKey(b));

const OwnershipColumn = (rowData: Template) => {
  if (rowData.isOrgTemplate) {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        <span className={css(styles.accessTypeIcon, "fa", "fa-globe")}></span>
        <span>Organization</span>
      </div>
    );
  }

  if (rowData.isPropertyTemplate) {
    return (
      <div style={{ whiteSpace: "nowrap" }}>
        <span className={css(styles.accessTypeIcon, "fa", "fa-home")}></span>
        <span
          className={rowData.properties.length > 1 ? tooltip.tooltip : ""}
          data-tooltip={rowData.properties.map((prop: { name: string; }) => prop.name).join(", ")}>
          {rowData.properties[0].name}
          {rowData.properties.length > 1 && "..."}
        </span>

      </div>
    );
  }

  return (
    <div style={{ whiteSpace: "nowrap" }}>
      <span className={css(styles.accessTypeIcon, "fa", "fa-user")}></span>
      <span>{rowData.user?.profile?.fullName || rowData.userId }</span>
    </div>
  );
};
const LOCAL_STORAGE_KEY = "template-table-state";
type TableFilterState = {
  orderBy?: number,
  orderDirection?: "asc" | "desc",
  searchText?: string
}
const storeTableState = (input: TableFilterState) => {
  const storedJson = localStorage.getItem(LOCAL_STORAGE_KEY) || "{}";
  const data = JSON.parse(storedJson);

  localStorage.setItem(
    LOCAL_STORAGE_KEY,
    JSON.stringify({ ...data, ...input }),
  );
};

const loadTableState = (): TableFilterState | null => {
  const storedJson = localStorage.getItem(LOCAL_STORAGE_KEY);

  if (!storedJson) return null;

  return JSON.parse(storedJson) || null;
};


const TemplateTable: React.FC<{
  viewer: Viewer,
  rowData: Template[],
  refetchTemplates: RefetchTemplates
}> = ({ rowData, viewer, refetchTemplates }) => {
  const pageSizeOptions = [5, 10, 15, 25, 50, 100];
  const pageSize = selectPageSize(Math.min(rowData.length, 25), pageSizeOptions);
  const tableState = loadTableState();
  const extraOptions = tableState && tableState.searchText ? (
    { searchText: tableState.searchText }) : {};
  const defaultSort = tableState && typeof tableState.orderBy === "number" && tableState.orderBy > -1 ? (
    { orderBy: tableState.orderBy, orderDirection: tableState.orderDirection }) : (
    { orderBy: 0, orderDirection: "asc" });
  const columns: Array<Column<Template>> = [
    { title: "Name",
      field: "name",
      render: ({ name, id }) => (<div style={{ maxWidth: "500px" }}>
        <Link to={`/dash/manager/settings/templates/update/${id}`}>{name}</Link>
      </div>) },
    { title: "Access", field: "access", render: OwnershipColumn, customSort: sortOwnershipType },
    { title: "SMS", field: "sms", sorting: false, render: SmsColumn },
    { title: "Email", field: "email", sorting: false, render: EmailColumn },
    { title: "Letter", field: "letter", sorting: false, render: MailColumn },
    { title: "Last Updated", field: "updatedAt", render: UpdatedAtColumn, customSort: sortUpdatedAt },
    { title: "Actions",
      field: "body",
      sorting: false,
      render: (data: Template) => (
        <ActionColumn rowData={data} viewer={viewer} refetchTemplates={refetchTemplates}/>) },
  ];
  columns[defaultSort.orderBy].defaultSort = (defaultSort.orderDirection || "asc") as "asc" | "desc";

  return (<MaterialTable
    onSearchChange={(searchText) => {
      storeTableState({ searchText });
    }}
    onOrderChange={(orderBy: number, orderDirection: "asc" | "desc") => {
      storeTableState({ orderBy, orderDirection });
    }}
    columns={columns}
    components={{ Container: Grid }}
    data={rowData}
    options={{
      pageSize,
      pageSizeOptions,
      draggable: false,
      showTitle: false,
      padding: "dense",
      search: true,
      toolbar: true,
      ...extraOptions,
    }}
  />);
};

export default TemplateTable;
